export interface TransformationFeature {
  name: string;
  text: string;
}

export interface Transformation {
  name: string;
  subtitle: string;
  benefit: TransformationFeature;
  drawback: TransformationFeature;
  source: 'void' | 'hf';
}

export const transformations: Transformation[] = [
  {
    name: 'Vampire',
    subtitle: 'Vampires are undead, fanged creatures that drink the blood of the living.',
    source: 'void',
    benefit: {
      name: 'Fangs',
      text: 'Make a Strength Roll to bite a target within Melee range, dealing d8 physical damage using your Proficiency.',
    },
    drawback: {
      name: 'Feed',
      text: 'On a successful Fangs attack against a living creature, you can mark a Stress to Feed. Place a number of tokens on this card equal to the number of Hit Points your target marks. You can hold up to 5 tokens at a time. Spend a token before an action roll to make your Fear Die a d20 instead. When you take a Long Rest, remove a token. If there are no tokens on this card, all your action and reaction rolls are made with disadvantage.',
    },
  },
  {
    name: 'Werewolf',
    subtitle: 'Werewolves are creatures who transform into supernatural wolves.',
    source: 'void',
    benefit: {
      name: 'Wolf Form',
      text: 'When you mark one or more Hit Points, you can also mark a Stress to enter your Wolf Form. While in this form, gain a d10 Wolf Die that you add to all attack and damage rolls. When you would gain a Hope while in Wolf Form, you mark a Stress instead.',
    },
    drawback: {
      name: 'Frenzy',
      text: 'When you mark your last Stress while in Wolf Form, you go into a Frenzy. Roll a number of d20s equal to your tier and automatically deal that much physical damage to all creatures within Very Close range. Then drop out of Wolf Form.',
    },
  },
  {
    name: 'Reanimated',
    subtitle: 'Reanimated are corpses who have been brought back to life.',
    source: 'void',
    benefit: {
      name: 'Stitch Up',
      text: 'During a rest, you can only clear Hit Points if you have access to remains from a recently deceased creature. Describe how using this material affects your appearance. You cannot clear Hit Points by any means except a downtime move or the Risk It All death move.',
    },
    drawback: {
      name: 'Corpse',
      text: "When you Risk It All on a death move, if you fail, you can permanently mark a Hit Point to succeed instead. When you do, you still use the Hope Die's value to clear Hit Points and Stress.",
    },
  },
  {
    name: 'Shapeshifter',
    subtitle: 'Shapeshifters are creatures that can change their physical form.',
    source: 'void',
    benefit: {
      name: 'Change Shape',
      text: 'During a long rest, you can spend a downtime move to swap your current ancestry with another. When you do, describe how your appearance changes.',
    },
    drawback: {
      name: 'Only Skin Deep',
      text: "When you Change Shape, you only gain the benefit of one of the ancestry's features, which you select when you choose the ancestry. You can spend a downtime move to switch which of your ancestry's features you gain the benefit of.",
    },
  },
  {
    name: 'Ghost',
    subtitle: 'Ghosts are a spirit trapped between the mortal realm and the veil of death.',
    source: 'void',
    benefit: {
      name: 'Spirit Form',
      text: 'Your physical form can shift between corporeal and incorporeal. Mark a Stress to transition into and out of your Spirit Form. While in Spirit Form, you can move through solid objects, are immune to physical damage, and take double magic damage. You can spend 2 Hope to attack or physically interact with the material world.',
    },
    drawback: {
      name: 'Ephemeral',
      text: 'Cross out a Hit Point slot when you take this card and when you increase your tier. When you mark your last Hit Point, you must choose Blaze of Glory as your death move.',
    },
  },
  {
    name: 'Demigod',
    subtitle: 'Demigods are creatures whose veins flow with the blood of the gods.',
    source: 'void',
    benefit: {
      name: 'Ichor of the Gods',
      text: 'Your advantage die is always a d10 instead of a d6.',
    },
    drawback: {
      name: 'Weight of Divinity',
      text: 'When you roll a failure with Fear, you must mark a Stress or give the GM an additional Fear.',
    },
  },

  /* ── Hope & Fear (final) versions. The printed cards don't label benefit and
     drawback, so each uses the Void card's split; Ghost's Ephemeral is the benefit. ── */
  {
    name: 'Vampire',
    subtitle: 'Vampires are undead creatures with sharp fangs who feed on the blood of the living.',
    source: 'hf',
    benefit: {
      name: 'Fangs',
      text: 'Make an attack using a trait of your choice to bite a target within Melee range. On a success, deal d6 physical damage using your Proficiency.',
    },
    drawback: {
      name: 'Feed',
      text: 'On a successful "Fangs" attack against a creature that can bleed, you can mark a Stress to feed. Place a number of tokens on this card equal to the number of Hit Points the target marks. You can hold up to 6 tokens at a time. Before you make an action roll, you can spend a token to make your Fear Die a d20. When you take a long rest, remove a token. While there are no tokens on this card, you make action and reaction rolls with disadvantage.',
    },
  },
  {
    name: 'Werewolf',
    subtitle: 'Werewolves are creatures who transform into large supernatural wolves.',
    source: 'hf',
    benefit: {
      name: 'Wolf Form',
      text: 'When you mark 1 or more Hit Points, you can mark a Stress to enter your Wolf Form. While in this form, you gain a 1d10 bonus to attack and damage rolls. When you roll with Hope while in Wolf Form, you must mark a Stress. Your Wolf Form lasts until you go into your "Howling Rampage" or take a rest.',
    },
    drawback: {
      name: 'Howling Rampage',
      text: 'When you mark your last Stress while in Wolf Form, you go into a rampage. Roll a number of d20s equal to your tier and deal that much physical damage to all creatures within Very Close range, then drop out of Wolf Form.',
    },
  },
  {
    name: 'Reanimated',
    subtitle: 'Reanimated are corpses who have been brought back to life.',
    source: 'hf',
    benefit: {
      name: "Won't Stay Dead",
      text: "When you choose the Risk It All death move and fail, you can permanently mark a Hit Point to succeed instead. When you do, you still use the Hope Die's value to clear Hit Points and Stress. When you permanently mark your last Hit Point, you pass through the veil of death.",
    },
    drawback: {
      name: 'Corpse',
      text: 'During a rest, you can clear Hit Points only if you have access to remains from a recently deceased creature. Describe how you use these materials to maintain your corpse.',
    },
  },
  {
    name: 'Shapeshifter',
    subtitle: 'Shapeshifters are creatures who can change their physical form.',
    source: 'hf',
    benefit: {
      name: 'Change Shape',
      text: 'During a rest, you can use a downtime move to swap your current ancestry with another. When you do, describe how your appearance changes.',
    },
    drawback: {
      name: 'Only Skin Deep',
      text: "You gain the benefit of only one of your chosen ancestry's features, which you select when you choose the ancestry. You can use a downtime move to choose a different feature from that ancestry.",
    },
  },
  {
    name: 'Ghost',
    subtitle: 'Ghosts are the spirits of the once-living who are bound to the Mortal Realm.',
    source: 'hf',
    benefit: {
      name: 'Ephemeral',
      text: 'Your body wavers in and out of being corporeal. You are resistant to physical damage, take double magic damage, and can mark 2 Stress to momentarily pass through a solid object.',
    },
    drawback: {
      name: 'Unfinished Business',
      text: 'Work with your GM to decide what purpose or desire keeps you bound to the Mortal Realm. When you fulfill it, you cross through the veil of death.',
    },
  },
  {
    name: 'Demigod',
    subtitle: 'Demigods are mortal creatures whose veins flow with the blood of the gods.',
    source: 'hf',
    benefit: {
      name: 'Gifted',
      text: 'You gain a +1 bonus to action, reaction, and damage rolls.',
    },
    drawback: {
      name: 'Weight of Divinity',
      text: 'When you fail a roll, you must mark a Stress or the GM gains a Fear.',
    },
  },
];
